import { React, useState, useEffect } from "react";
import {
  Grid,
  Pagination,
  useMediaQuery,
  useTheme,
  styled,
  Typography,
} from "@mui/material";
import { S3Data } from "../../S3Data";
import Footer from "../footer/Footer";

const StyledRootContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
});

const IntroCard = styled("section")(({ theme }) => ({
  width: "min(1100px, 100%)",
  marginBottom: "1.75rem",
  padding: "1.8rem 2rem",
  borderRadius: "2rem",
  background: "rgba(255, 248, 239, 0.82)",
  border: "1px solid rgba(159, 104, 72, 0.14)",
  boxShadow: "0 20px 48px rgba(90, 60, 41, 0.1)",
  [theme.breakpoints.down("sm")]: {
    padding: "1.2rem 1rem",
    borderRadius: "1.5rem",
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontSize: "0.82rem",
  textTransform: "uppercase",
  letterSpacing: "0.22em",
  color: theme.palette.primary.main,
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Fira Sans Extra Condensed', sans-serif",
  fontSize: "clamp(2.5rem, 5vw, 4rem)",
  lineHeight: 0.95,
  marginTop: "0.35rem",
  color: theme.palette.text.primary,
}));

const IntroText = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: 1.8,
  color: theme.palette.text.secondary,
  maxWidth: "42rem",
  marginTop: "0.85rem",
}));

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  width: "min(1100px, 100%)",
  margin: "0 auto",
  justifyContent: "center",
  gap: "1.75rem",
  [theme.breakpoints.down("sm")]: {
    gap: "1rem",
  },
}));

const GalleryItem = styled(Grid)(({ theme }) => ({
  paddingLeft: "0 !important",
  alignSelf: "stretch",
}));

const ArtworkCard = styled("div")(({ theme }) => ({
  height: "100%",
  padding: "0.95rem",
  borderRadius: "1.85rem",
  background: "rgba(255, 252, 247, 0.88)",
  border: "1px solid rgba(159, 104, 72, 0.12)",
  boxShadow: "0 16px 34px rgba(96, 63, 43, 0.08)",
}));

const ArtworkImage = styled("img")({
  width: "100%",
  borderRadius: "1.25rem",
  objectFit: "cover",
});

const StyledGridPagination = styled(Pagination)(({ theme }) => ({
  marginTop: "1.25rem",
  "& ul": {
    justifyContent: "center",
    gap: "0.3rem",
    "& li button": {
      fontSize: "1rem",
      fontWeight: 700,
      color: theme.palette.text.secondary,
      backgroundColor: "rgba(255, 248, 239, 0.7)",
    },
    "& li .Mui-selected": {
      backgroundColor: `${theme.palette.primary.main} !important`,
      color: "#fff8f1",
    },
  },
}));

const baseUrl = "https://daily-sarah.s3.amazonaws.com/";

function Gallery() {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down("sm"));
  const [items, setItems] = useState([]);

  const [page, setPage] = useState(1);
  const itemsPerPage = 4;
  const pageCount = Math.ceil(items.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    async function fetchData() {
      const data = await S3Data("gallery2");
      setItems(data?.Contents?.slice(1) || []);
    }
    fetchData();
  }, []);

  const getMeta = (url, cb) => {
    const img = new Image();
    img.onload = () => cb(null, img);
    img.onerror = (err) => cb(err);
    img.src = url;
  };

  useEffect(() => {
    for (let i = 1; i < items.length; i++) {
      getMeta(`${baseUrl}${items[i].Key}`, (err, img) => {
        if (err) {
          return;
        }
        let aspectRatio = img.naturalWidth / img.naturalHeight;
        items[i].aspectRatio = aspectRatio;
      });
    }
  }, [items]);

  return (
    <div style={{ width: "100%" }}>
      <StyledRootContainer>
        <IntroCard>
          <Eyebrow>Gallery</Eyebrow>
          <Heading>Sarah&apos;s visual work in one place.</Heading>
          <IntroText>
            A collection of paintings, drawings, and digital pieces.
          </IntroText>
        </IntroCard>
        <StyledGridContainer container spacing={0}>
          {items
            .slice((page - 1) * itemsPerPage, page * itemsPerPage)
            .map((item) => {
              return (
                <GalleryItem item key={item.ETag} xs={sm ? 12 : null}>
                  <ArtworkCard>
                    <ArtworkImage
                      alt=""
                      style={
                        item.aspectRatio > 0
                          ? {
                              width: (230 * item.aspectRatio).toString() + "px",
                              height: "230px",
                            }
                          : {
                              width: "230px",
                              height: (230 * item.aspectRatio).toString() + "px",
                            }
                      }
                      src={`${baseUrl}${item.Key}`}
                    />
                  </ArtworkCard>
                </GalleryItem>
              );
            })}
          <Grid item xs={12} sx={{ paddingLeft: "0 !important" }}>
            <StyledGridPagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
            />
          </Grid>
        </StyledGridContainer>
      </StyledRootContainer>
      <Footer />
    </div>
  );
}

export default Gallery;
